import React, { useContext, useState, useEffect } from "react";
import style from "../styles/cart.module.scss";
import Banner from "../components/shared/Banner";
import { Link, useNavigate } from "react-router-dom";
import { IoIosArrowForward } from "react-icons/io";
import CartProduct from "../components/shared/CartProduct";
import { useCart } from "react-use-cart";
import { GlobalContext } from "../context/GlobalContext";
import { OrbitProgress } from "react-loading-indicators";
import { trackBeginCheckout, trackPurchase } from "../utils/tracking";
import api from "../baseApi";
import { toast } from "react-toastify";
import withHelmet from "../seo/withHelmet";
import { cartHelmetProps } from "../seo/helmetProps";
import FreeShipping from "../components/shared/FreeShipping";

const Cart = () => {
  const navigate = useNavigate();
  const { items, emptyCart } = useCart();
  const { makeOrder } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    address: "",
    city: "",
    postalCode: "",
    phoneNumber: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Ju lutem jepni kodin promocional");
      return;
    }

    setPromoCodeLoading(true);
    try {
      const response = await api.post("/promo-codes/validate", {
        code: promoCode.trim(),
      });

      if (response.data.valid) {
        setAppliedPromoCode(response.data.promoCode);
        toast.success("Kodi promocional u aplikua me sukses!");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Kodi promocional nuk u gjet");
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error?.message;
        if (errorMessage === "Promo code is required") {
          toast.error("Kodi promocional është i detyrueshëm");
        } else if (errorMessage === "Promo code is not active") {
          toast.error("Kodi promocional nuk është aktiv");
        } else if (errorMessage === "Promo code has expired") {
          toast.error("Kodi promocional ka skaduar");
        } else if (
          errorMessage === "Promo code has reached its maximum usage limit"
        ) {
          toast.error(
            "Kodi promocional ka arritur kufirin e maksimumit të përdorimit"
          );
        } else {
          toast.error("Kodi promocional nuk është i vlefshëm");
        }
      } else {
        toast.error("Ndodhi një gabim gjatë validimit të kodit promocional");
      }
      setAppliedPromoCode(null);
    } finally {
      setPromoCodeLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromoCode(null);
    setPromoCode("");
    toast.info("Kodi promocional u hoq");
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim())
      newErrors.fullName = "Emri dhe mbiemri është i detyrueshëm.";
    if (!formData.address.trim())
      newErrors.address = "Adresa është e detyrueshme.";
    if (!formData.city.trim()) newErrors.city = "Qyteti është i detyrueshëm.";
    // if (!formData.postalCode.trim() || !/^\d{4,5}$/.test(formData.postalCode))
    //   newErrors.postalCode = "Kodi postar duhet të përmbajë 4-5 shifra.";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Numri i telefonit është i detyrueshëm.";
    if (!formData.country.trim())
      newErrors.country = "Shteti është i detyrueshëm.";
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!formData.email) {
      newErrors.email = "Adresa e emailit është e detyrueshme.";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Ju lutem jepni një adresë emaili të vlefshme.";
    }
    return newErrors;
  };

  function calculateTotal(products) {
    let totalAmount = 0;

    products.forEach((product) => {
      if (product.discount > 0) {
        product.itemTotal =
          (product.price - (product.price * product.discount) / 100) *
          product.quantity;
      } else {
        product.itemTotal = product.price * product.quantity;
      }
      totalAmount += product.itemTotal;
    });

    return totalAmount;
  }

  // false = free shipping, true = charge shippingFee
  function checkDeliveryStatus(orderTotalBeforeShipping, country) {
    const qualifiesFree = orderTotalBeforeShipping >= 50;
    if (country === "Kosovë" || country === "Shqipëri") {
      return qualifiesFree ? false : true;
    }
    // No country yet: still respect 50€+ free so we don’t falsely charge or use the old €10 bug
    if (!country?.trim()) {
      return qualifiesFree ? false : true;
    }
    return true;
  }

  function getShippingFee(country) {
    if (country === "Shqipëri") {
      return 5;
    }
    return 2.5; // Kosovo and other countries
  }

  function processOrder(products, country, promoCode = null) {
    const subtotalWithoutDiscount = calculateTotal(products);
    let totalAmount = calculateTotal(products);
    const shippingFee = getShippingFee(country);

    // Apply promo code discount before free-shipping check (threshold = goods total after promo)
    let discountAmount = 0;
    if (promoCode) {
      // Calculate discount only on products without discount
      let productsWithoutDiscount = 0;
      products.forEach((product) => {
        if (product.discount === 0) {
          productsWithoutDiscount += product.price * product.quantity;
        }
      });

      if (promoCode.discountType === "percentage") {
        discountAmount =
          (productsWithoutDiscount * promoCode.discountValue) / 100;
      } else if (promoCode.discountType === "fixed") {
        discountAmount = promoCode.discountValue;
      }
      totalAmount = Math.max(0, totalAmount - discountAmount);
    }

    const deliveryStatus = checkDeliveryStatus(totalAmount, country);

    return {
      products,
      subtotalWithoutDiscount,
      totalAmount,
      deliveryStatus,
      discountApplied: false,
      shippingFee,
      promoCode: promoCode?.code || null,
      discountAmount,
    };
  }

  // Track initiate checkout when cart page loads with items
  useEffect(() => {
    if (items.length > 0) {
      const defaultOrderData = processOrder(items, "Kosovë", null);
      const totalValue = defaultOrderData.deliveryStatus
        ? defaultOrderData.totalAmount + defaultOrderData.shippingFee
        : defaultOrderData.totalAmount;
      trackBeginCheckout(items, totalValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only track once when component mounts with items

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (items.some((item) => item.notInStock)) {
      toast.error("Një produkt në shportë është i shitur");
      setLoading(false);
      return;
    }
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setErrors({});

      const data = {
        data: {
          userEmail: formData.email,
          userAddress: `${formData.address}, ${formData.city}, ${formData.postalCode}, ${formData.country} `,
          userPhone: formData.phoneNumber,
          OrderProduct: items.map((item) => {
            return {
              quantity: item.quantity,
              product: item.documentId,
            };
          }),
          total: processOrder(items, formData.country, appliedPromoCode)
            .deliveryStatus
            ? (
                processOrder(items, formData.country, appliedPromoCode)
                  .totalAmount +
                processOrder(items, formData.country, appliedPromoCode)
                  .shippingFee
              ).toFixed(2)
            : processOrder(
                items,
                formData.country,
                appliedPromoCode
              ).totalAmount.toFixed(2),
          name: formData.fullName,
          delivery: processOrder(items, formData.country, appliedPromoCode)
            .deliveryStatus,
          discount: processOrder(items, formData.country, appliedPromoCode)
            .discountApplied,
          subtotal: processOrder(
            items,
            formData.country,
            appliedPromoCode
          ).subtotalWithoutDiscount.toFixed(2),
          promoCode: processOrder(items, formData.country, appliedPromoCode)
            .promoCode,
        },
      };
      const orderData = processOrder(items, formData.country, appliedPromoCode);
      const totalValue = orderData.deliveryStatus
        ? orderData.totalAmount + orderData.shippingFee
        : orderData.totalAmount;
      const shippingFee = orderData.deliveryStatus ? orderData.shippingFee : 0;

      // Track purchase
      const transactionId = `order_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      trackPurchase(
        transactionId,
        items,
        parseFloat(totalValue.toFixed(2)),
        parseFloat(shippingFee.toFixed(2)),
        0
      );

      await makeOrder(data);

      navigate("/thankyou");
      emptyCart();
    }
    setLoading(false);
  };
  // console.log("subtotal", processOrder(items).subtotalWithoutDiscount);
  // console.log("total", processOrder(items).totalAmount);
  // console.log("deliveryStatus", processOrder(items).deliveryStatus);
  // console.log("discountApplied", processOrder(items).discountApplied);

  return (
    <div className={style.wrapper}>
      <Banner
        current={"Shporta"}
        title={"Shporta Juaj"}
        from={[
          { name: "Ballina", link: "/" },
          { name: "Shop", link: "/shop" },
        ]}
      />
      {items.length > 0 ? (
        <div className={style.cart}>
          <div className={style.products}>
            <FreeShipping
              total={
                processOrder(items, formData.country, appliedPromoCode)
                  .totalAmount
              }
            />
            {items.map((item, key) => {
              return <CartProduct key={key} product={item} />;
            })}
          </div>
          <div className={style.billingInfo}>
            <form onSubmit={handleSubmit}>
              <h1>Dërgo Porosinë</h1>
              <div>
                <label>Emri dhe Mbiemri</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Shkruani emrin dhe mbiemrin tuaj"
                />
                {errors.fullName && (
                  <p style={{ color: "red" }}>{errors.fullName}</p>
                )}
              </div>
              <div>
                <label>Emaili</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Shkruani emailin tuaj"
                />
                {errors.email && <p style={{ color: "red" }}>{errors.email}</p>}
              </div>
              <div>
                <label>Adresa</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Shkruani adresën tuaj"
                />
                {errors.address && (
                  <p style={{ color: "red" }}>{errors.address}</p>
                )}
              </div>
              <div>
                <label>Qyteti</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Shkruani qytetin tuaj"
                />
                {errors.city && <p style={{ color: "red" }}>{errors.city}</p>}
              </div>
              <div>
                <label>Kodi Postar(opsional)</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  placeholder="Shkruani kodin postar"
                />
                {errors.postalCode && (
                  <p style={{ color: "red" }}>{errors.postalCode}</p>
                )}
              </div>
              <div>
                <label>Numri i Telefonit</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Shkruani numrin tuaj të telefonit"
                />
                {errors.phoneNumber && (
                  <p style={{ color: "red" }}>{errors.phoneNumber}</p>
                )}
              </div>
              <div>
                <label>Shteti</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option value="">Shteti</option>
                  <option value="Kosovë">Kosovë</option>
                  <option value="Shqipëri">Shqipëri</option>
                </select>
                {errors.country && (
                  <p style={{ color: "red" }}>{errors.country}</p>
                )}
              </div>
              <div>
                <label>Kodi promocional</label>
                <div className={style.promoCodeInput}>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Shkruani kodin promocional"
                    disabled={!!appliedPromoCode || promoCodeLoading}
                  />
                  {appliedPromoCode ? (
                    <button
                      type="button"
                      onClick={removePromoCode}
                      className={style.removePromoBtn}
                    >
                      Hiq
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={validatePromoCode}
                      disabled={promoCodeLoading || !promoCode.trim()}
                      className={style.applyPromoBtn}
                    >
                      {promoCodeLoading ? "Duke validuar..." : "Apliko"}
                    </button>
                  )}
                </div>
                {appliedPromoCode && (
                  <div className={style.promoCodeApplied}>
                    <p>
                      Kodi promocional "{appliedPromoCode.code}" u aplikua (
                      {appliedPromoCode.discountType === "percentage"
                        ? `-${appliedPromoCode.discountValue}%`
                        : `-${appliedPromoCode.discountValue}€`}
                      )
                    </p>
                    <p style={{ fontSize: "12px", marginTop: "5px" }}>
                      Zbritja aplikohet vetëm në produktet pa zbritje
                    </p>
                  </div>
                )}
              </div>
              <div className={style.billingDetails}>
                <h3>
                  Nëntotali:{" "}
                  {processOrder(
                    items,
                    formData.country,
                    appliedPromoCode
                  ).subtotalWithoutDiscount.toFixed(2)}
                  €
                </h3>
                {appliedPromoCode && (
                  <h4>
                    Zbritje nga kodi promocional:{" "}
                    {processOrder(
                      items,
                      formData.country,
                      appliedPromoCode
                    ).discountAmount.toFixed(2)}
                    €
                  </h4>
                )}
                {processOrder(items, formData.country, appliedPromoCode)
                  .discountApplied ? (
                  <h4>
                    Zbritje: -10%{" "}
                    <span>
                      Zbritja aplikohet vetëm në produktet pa zbritje!
                    </span>
                  </h4>
                ) : (
                  <></>
                )}
                <h4>
                  Posta:{" "}
                  {processOrder(items, formData.country, appliedPromoCode)
                    .deliveryStatus
                    ? `${
                        processOrder(items, formData.country, appliedPromoCode)
                          .shippingFee
                      }€`
                    : "Falas"}{" "}
                  <span>Posta falas për blerjet mbi 50€!</span>
                </h4>
                <h2>
                  Totali:{" "}
                  {processOrder(items, formData.country, appliedPromoCode)
                    .deliveryStatus
                    ? (
                        processOrder(items, formData.country, appliedPromoCode)
                          .totalAmount +
                        processOrder(items, formData.country, appliedPromoCode)
                          .shippingFee
                      ).toFixed(2)
                    : processOrder(
                        items,
                        formData.country,
                        appliedPromoCode
                      ).totalAmount.toFixed(2)}
                  €
                </h2>
              </div>
              {loading ? (
                <div className={style.loader}>
                  <OrbitProgress
                    color="#5E718C"
                    size="small"
                    text=""
                    textColor=""
                  />
                </div>
              ) : (
                <button type="submit">Dërgo</button>
              )}
            </form>
          </div>
        </div>
      ) : (
        <div className={style.empty}>
          <h1>Momentalisht nuk keni zgjedhur asnjë produkt</h1>
          <Link to={"/shop"}>
            Shop <IoIosArrowForward fontWeight={"bold"} />
          </Link>
        </div>
      )}
    </div>
  );
};

export default withHelmet(Cart, cartHelmetProps);
